import React from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import { ENUM_VALUE_TYPE, FULL_LAYOUT_TYPE } from './Types';
import './DateFilterEditor.scss';

const TODAY = 'today';
const LAST_24_HOURS = '24h';
const LAST_7_DAYS = '7d';
const LAST_30_DAYS = '30d';
const DAY_IN_MILLIS = 24 * 3600 * 1000;
const datePresets = [LAST_24_HOURS, LAST_7_DAYS, LAST_30_DAYS, TODAY];

const DATE_FORMAT = moment.localeData().longDateFormat('L');

const isToday = (someDate) => {
  if (someDate === TODAY) {
    return true;
  }
  if (!someDate) {
    return false;
  }
  const today = new Date();
  return someDate.getDate() === today.getDate()
    && someDate.getMonth() === today.getMonth()
    && someDate.getFullYear() === today.getFullYear();
};

const getTimeRangeChipLabel = (value) => {
  const { from, to } = value;
  if (from === LAST_24_HOURS) {
    return 'Last 24 hours';
  }
  if (from === LAST_7_DAYS) {
    return 'Last 7 days';
  }
  if (from === LAST_30_DAYS) {
    return 'Last 30 days';
  }
  if (to === TODAY) {
    return `From ${moment(from).fromNow()}`;
  }
  return `From ${moment(from).format(DATE_FORMAT)} to ${moment(to).format(DATE_FORMAT)}`;
};

const DateFilterEditor = (props) => {
  const { value = {}, editor, onValueChange } = props;
  const now = new Date().getTime();
  const useTimeRange = Boolean(value.from);
  let timeRangeFrom = value.from;
  let timeRangeTo = value.to;
  if (!timeRangeFrom) {
    const today = new Date();
    const daysBefore = new Date();
    daysBefore.setDate(today.getDate() - 3);
    timeRangeFrom = daysBefore;
    timeRangeTo = today;
  } else if (timeRangeFrom === LAST_24_HOURS) {
    timeRangeFrom = new Date(now - DAY_IN_MILLIS);
    timeRangeTo = now;
  } else if (timeRangeFrom === LAST_7_DAYS) {
    timeRangeFrom = new Date(now - 7 * DAY_IN_MILLIS);
    timeRangeTo = now;
  } else if (timeRangeFrom === LAST_30_DAYS) {
    timeRangeFrom = new Date(now - 30 * DAY_IN_MILLIS);
    timeRangeTo = now;
  } else if (timeRangeTo === TODAY) {
    timeRangeTo = now;
  }
  const handleDatesChange = (from, to) => {
    // Keep presets specifications when possible.
    const newFrom = from || (value.from && value.from) || undefined;
    const newTo = to || (value && value.to) || (from && TODAY) || undefined;
    onValueChange({ from: newFrom, to: isToday(newTo) ? TODAY : newTo });
  };
  const handleFromDateChange = (dateIOType, s) => {
    if (dateIOType) {
      const date = dateIOType.toDate ? dateIOType.toDate() : moment(s).toDate();
      handleDatesChange(date, undefined);
    }
  };
  const handleToDateChange = (dateIOType, s) => {
    if (dateIOType) {
      const date = dateIOType.toDate ? dateIOType.toDate() : moment(s).toDate();
      handleDatesChange(undefined, isToday(date) ? TODAY : date);
    }
  };
  const handleUseTimeRange = () => {
    onValueChange(value.from ? undefined : { from: timeRangeFrom, to: timeRangeTo });
  };
  const handleLast24 = () => {
    onValueChange({ from: LAST_24_HOURS, to: TODAY });
  };
  const handleLast7Days = () => {
    onValueChange({ from: LAST_7_DAYS, to: TODAY });
  };
  const handleLast30Days = () => {
    onValueChange({ from: LAST_30_DAYS, to: TODAY });
  };
  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <div className="grbrsm-date-filter-editor">
        <div className="grbrsm-date-filter-title">
          <FormControlLabel
            control={(
              <Checkbox
                checked={useTimeRange}
                onChange={handleUseTimeRange}
                value="use"
                color="primary"
              />
            )}
            label={editor.title || 'Time range'}
          />
        </div>
        <div className="grbrsm-data-filter-editor-body">
          <div className="grbrsm-date-filter-range-editors">
            <KeyboardDatePicker
              clearable
              value={timeRangeFrom}
              onChange={handleFromDateChange}
              maxDate={now}
              disabled={!useTimeRange}
              format="MM/DD/YYYY"
            />
            <div className="grbrsm-date-separator">
              <hr />
            </div>
            <KeyboardDatePicker
              clearable
              value={timeRangeTo}
              onChange={handleToDateChange}
              minDate={timeRangeFrom}
              disabled={!useTimeRange}
              format={DATE_FORMAT}
            />
          </div>
          <div className="grbrsm-date-filter-presets">
            <Typography variant="caption">Presets:</Typography>
            <div className="grbrsm-date-filter-buttons">
              <Button variant="outlined" onClick={handleLast24} disabled={!useTimeRange}>Last 24 hours</Button>
              <Button variant="outlined" onClick={handleLast7Days} disabled={!useTimeRange}>Last 7 days</Button>
              <Button variant="outlined" onClick={handleLast30Days} disabled={!useTimeRange}>Last 30 days</Button>
            </div>
          </div>
        </div>
      </div>
    </MuiPickersUtilsProvider>
  );
};

DateFilterEditor.propTypes = {
  value: PropTypes.shape({}),
  editor: PropTypes.shape({
    title: PropTypes.string,
  }).isRequired,
  onValueChange: PropTypes.func.isRequired,
};

DateFilterEditor.defaultProps = {
  value: undefined,
};

// Workaround a wrong naming of the DateFilterEditor component, which is not a property editor
const convertToMillis = (s) => {
  if (s === TODAY) return new Date().getTime();
  if (s === LAST_24_HOURS) return new Date().getTime() - DAY_IN_MILLIS;
  if (s === LAST_7_DAYS) return new Date().getTime() - 7 * DAY_IN_MILLIS;
  if (s === LAST_30_DAYS) return new Date().getTime() - 30 * DAY_IN_MILLIS;
  return s.getTime();
};

const dateToString = (date) => {
  return datePresets.indexOf(date) >= 0 ? date : date.getTime();
};

const stringToDate = (s) => {
  return datePresets.indexOf(s) < 0 ? new Date(parseInt(s, 10)) : s;
};

const createTimeRangeEditor = options => ({
  title: options && options.title || 'Time range',
  name: options && options.name || 'timerange',
  keys: options && options.keys,
  type: ENUM_VALUE_TYPE,
  layout: FULL_LAYOUT_TYPE,
  Component: DateFilterEditor,
  getValueLabel: getTimeRangeChipLabel,
  canDeleteValue: () => true, // Any chip generated by this editor can be deleted to specify not using time range filtering
  toFilter: options && options.toFilter
    ? query => options.toFilter(query, { stringToDate })
    : (query) => {
      const [from, to] = query.split(',');
      return { from: stringToDate(from), to: stringToDate(to) };
    },
  toQuery: options && options.toQuery
    ? f => options.toQuery(f, { dateToString, convertToMillis })
    : (timerange) => {
      return `${dateToString(timerange.from)},${dateToString(timerange.to)}`;
    },
  sameFilters: (p1, p2) => {
    const t1 = options.keys ? p1.timerange : p1;
    const t2 = options.keys ? p2.timerange : p2;
    const sameValues = (v1, v2) => {
      const s1 = typeof v1 === 'string';
      const s2 = typeof v2 === 'string';
      if (s1 === s2) {
        return s1 ? v1 === v2 : v1.getTime() === v2.getTime();
      }
      const millis1 = s1 ? convertToMillis(v1) : v1.getTime();
      const millis2 = s2 ? convertToMillis(v2) : v2.getTime();
      return millis1 === millis2;
    };
    return sameValues(t1.from, t2.from) && sameValues(t1.to, t2.to);
  },
  createObjectFilter: options && options.createObjectFilter
    ? (f) => {
        return options.createObjectFilter(f, { convertToMillis });
      }
    : (param) => {
      const timerange = options.keys ? param.timerange : param;
      if (!timerange) return undefined;

      const from = convertToMillis(timerange.from);
      const to = convertToMillis(timerange.to);
      return (o) => {
        const d = options.getObjectDate(o).getTime();
        return d >= from && d <= to;
      };
    },
});

export { DateFilterEditor as default, getTimeRangeChipLabel, createTimeRangeEditor };
